import { Header } from 'components/Header';
import { CustomisableTable } from 'components/UI/CustomisableTable';
import { Flex } from 'components/UI/Flex';
import { Grid } from 'components/UI/Grid';
import { SearchInput } from 'components/UI/Inputs/SearchInput';
import { Suspend } from 'components/UI/Suspend';
import { UserCard } from 'components/UI/UserCard';
import { TransferProjectOwnershipModal } from 'components/Account/EnterpriseProjects/TransferProjectOwnershipModal/TransferProjectOwnershipModal';
import { FreezeProjectModal } from 'features/admin-actions/project-management/FreezeProjectModal';
import { UnfreezeProjectModal } from 'features/admin-actions/project-management/UnfreezeProjectModal';
import { splitString } from 'helpers/strings';
import {
	useAllEnterpriseOwnedProjects,
	useProjectId,
	useProjectMetadataDefinition,
	useSubscriptionRules,
	useTranslation,
	useUsername
} from 'hooks/store';
import { useEffect, useMemo, useState } from 'react';

import { ProjectOwnerDetails } from 'store/data/projects';
import {
	CustomCellType,
	CustomisableTableColumn,
	CustomisableTableRow,
	GenericMap,
	LanguageType,
	TableFilterType,
	TableName
} from 'types/index';
import { EnterpriseProject } from 'store/account/enterprise';
import { ProjectSettingsDropdown } from 'components/Account/EnterpriseProjects/TransferProjectOwnershipDropdown/ProjectSettingsDropdown';
import { useNavigate } from 'react-router-dom';
import { notEmpty } from 'helpers/arrays';
import { Dropdown } from '../../../components/UI/Dropdown';
import { Icon } from '../../../components/UI/Icons';
import { Svgs } from '../../../environment';
import { Container } from './EnterpriseAdminProjectsPage.style';
import { useGetAllUsersInEnterpriseProjects } from './useGetAllUsersInEnterpriseProjects';
import { exportProjectCollaborators } from '../../../helpers/projects/exports/exportProjectCollaborators';
import { exportEnterpriseProjects } from '../../../helpers/projects/exports/exportEnterpriseProjects';
import { useAlerts } from '../../../hooks/ui';
import { useGetUserQuery } from '../../../features/data/useGetUserQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIsCodelistFeatureEnabled } from '../../../features/codelist/useIsCodeListEnabled';
import { useLanguage } from '../../../features/hooks/useLanguage';

export function EnterpriseAdminProjectsPage() {
	const { translate } = useTranslation();
	const language = useLanguage();
	const dictionary = DICTIONARY[language];
	const navigate = useNavigate();

	const loggedInUserId = useUsername();

	const { exportProjects } = useFlags();

	const [{ data: projects, loading: loadingEnterpriseProjects }, getAllEnterpriseOwnedProjects] =
		useAllEnterpriseOwnedProjects();

	const [searchTerm, setSearchTerm] = useState('');
	const [filteredProjects, setFilteredProjects] = useState(projects);
	const [, setProjectId] = useProjectId();

	const [projectToTransfer, setProjectToTransfer] = useState<EnterpriseProject | null>(null);
	const [projectForFreezeModal, setProjectForFreezeModal] = useState<EnterpriseProject | null>(
		null
	);
	const [projectForUnfreezeModal, setProjectForUnfreezeModal] =
		useState<EnterpriseProject | null>(null);

	const projectsSplitSearchTerm = useMemo(() => splitString(searchTerm), [searchTerm]);

	const [activeDropownByProjectId, setActiveDropownByProjectId] = useState<string | null>(null);

	const { canTransferProjectOwnership } = useSubscriptionRules();
	const [{ data: projectMetadataDefinition }] = useProjectMetadataDefinition();
	const [projectMetadataCategoriesDict, setProjectMetadataCategoriesDict] = useState<
		Record<string, string>
	>({});

	useEffect(() => {
		if (!projectMetadataDefinition?.metadataDefinition) return;

		const projectTypeField = projectMetadataDefinition.metadataDefinition.find(
			field => field.name === 'metadata_projectType'
		);

		if (!projectTypeField?.categories) return;

		const categoriesDict = projectTypeField.categories.reduce((acc, category) => {
			acc[category.id] = category.name;
			return acc;
		}, {} as Record<string, string>);

		setProjectMetadataCategoriesDict(categoriesDict);
	}, [projectMetadataDefinition]);

	function onDropdownVisible(userId: string | null) {
		setActiveDropownByProjectId(userId);
	}

	useEffect(() => {
		getAllEnterpriseOwnedProjects();
	}, []);

	// SEARCH FILTERING
	useEffect(() => {
		let resultData: EnterpriseProject[] = [];

		if (!projectsSplitSearchTerm.length || !projects.length) {
			resultData = projects;
		} else {
			resultData = projects.filter(project => {
				let matches = true;

				const keywords = [
					project.projectName.toLowerCase().trim(),
					project.projectOwnerDetails.userFirstName?.toLowerCase().trim(),
					project.projectOwnerDetails.userSirName?.toLowerCase().trim(),
					project.projectOwnerDetails.organization?.toLowerCase().trim(),
					project.status?.toLowerCase().trim()
				].filter(Boolean) as string[];

				projectsSplitSearchTerm.forEach(term => {
					let termFound = false;

					keywords.forEach(keyword => {
						if (keyword.indexOf(term) !== -1) termFound = true;
					});

					if (!termFound) matches = false;
				});

				return matches;
			});
		}
		setFilteredProjects(resultData);
	}, [projects, projectsSplitSearchTerm]);

	function userNameComponent(user: ProjectOwnerDetails) {
		return (
			<UserCard.Table
				userId={user.userId}
				userData={{
					userFirstName: user.userFirstName,
					userSirName: user.userSirName,
					emailAddress: user.emailAddress
				}}
			/>
		);
	}

	const [customCells, setCustomCells] = useState<CustomCellType>({});
	const [actionCellByRowId, setActionCellByRowId] = useState<GenericMap<JSX.Element>>({});
	const isCodeListFeatureEnabled = useIsCodelistFeatureEnabled();

	// populate tableItems and customCells with data
	const tableItems = useMemo(() => {
		const items: CustomisableTableRow[] = [];
		filteredProjects.forEach(project => {
			const projectTypeId = project.metadata?.projectType as '1' | '2';
			const projectTypeDictKey =
				projectTypeId !== undefined
					? projectMetadataCategoriesDict[projectTypeId]
					: undefined;
			const projectTypeLabel = projectTypeDictKey
				? translate(_ => projectTypeDictKey)
				: undefined;

			const item = {
				id: project.projectId,
				projectName: project.projectName,
				createdByUser:
					project.projectOwnerDetails.userFirstName +
					' ' +
					project.projectOwnerDetails.userSirName,
				collaborators: project.collaborators,
				codeList: project.keyId ? dictionary.enabled : dictionary.disabled,
				organization: project.projectOwnerDetails?.organization ?? '',
				suborganization: project.projectOwnerDetails?.subOrganization ?? '',
				status: project.status.toString(),
				archiveNumber: project.metadata?.archiveNumber ?? '',
				projectType: projectTypeLabel
			};

			items.push(item);
			const customCell: GenericMap<string | JSX.Element> = {};

			if (project.projectOwnerDetails) {
				customCell.createdByUser = userNameComponent(project.projectOwnerDetails);
			}

			setCustomCells(prev => ({ ...prev, [item.id]: customCell }));

			setActionCellByRowId(prev => ({
				...prev,
				[item.id]: (
					<ProjectSettingsDropdown
						projectStatus={project.status}
						onDropdownVisible={(visible: boolean) =>
							onDropdownVisible(visible ? project.projectId : null)
						}
						projectName={project.projectName}
						showTransferOwnership={
							project.projectOwnerDetails &&
							project.projectOwnerDetails.userId !== loggedInUserId &&
							canTransferProjectOwnership
						}
						onTransferProject={() => {
							setProjectToTransfer(project);
						}}
						onFreezeProject={() => {
							setProjectForFreezeModal(project);
						}}
						onUnfreezeProject={() => {
							setProjectForUnfreezeModal(project);
						}}
						onViewCollaboratorsClicked={() => {
							setProjectId(project.projectId);
							navigate(`/projects/${project.projectId}/collaborators`);
						}}
					/>
				)
			}));
		});
		return items;
	}, [
		filteredProjects,
		canTransferProjectOwnership,
		projectMetadataCategoriesDict,
		isCodeListFeatureEnabled,
		dictionary
	]);

	const showSuborganization = useMemo(
		() => !!filteredProjects.find(project => !!project.projectOwnerDetails?.subOrganization),
		[filteredProjects]
	);
	const showOrganization = useMemo(
		() => !!filteredProjects.find(project => !!project.projectOwnerDetails?.organization),
		[filteredProjects]
	);
	const showArchiveNumber = useMemo(
		() => !!filteredProjects.find(project => !!project.metadata?.archiveNumber),
		[filteredProjects]
	);
	const showProjectType = useMemo(
		() => !!filteredProjects.find(project => !!project.metadata?.projectType),
		[filteredProjects]
	);

	const columns: CustomisableTableColumn[] = useMemo(
		() =>
			[
				{
					name: 'projectName',
					label: translate(({ enterpriseAdmin }) => enterpriseAdmin.projects.projects),
					filterType: TableFilterType.Text
				},
				{
					name: 'createdByUser',
					label: translate(({ enterpriseAdmin }) => enterpriseAdmin.projects.ownerName),
					filterType: TableFilterType.Text
				},
				isCodeListFeatureEnabled
					? {
							name: 'codeList',
							label: dictionary.pii,
							filterType: TableFilterType.Checkbox
					  }
					: null,
				{
					name: 'collaborators',
					label: translate(
						({ enterpriseAdmin }) => enterpriseAdmin.projects.collaborators
					),
					filterType: TableFilterType.Numeric
				},
				showOrganization
					? {
							name: 'organization',
							label: translate(
								({ enterpriseAdmin }) => enterpriseAdmin.projects.organization
							),
							filterType: TableFilterType.Checkbox,
							isOptional: true
					  }
					: null,
				showSuborganization
					? {
							name: 'suborganization',
							label: translate(
								({ accountUM }) => accountUM.userDetails.suborganization
							),
							filterType: TableFilterType.Checkbox,
							isOptional: true
					  }
					: null,
				{
					name: 'status',
					label: translate(({ enterpriseAdmin }) => enterpriseAdmin.projects.status),
					filterType: TableFilterType.Checkbox,
					isOptional: true
				},
				showArchiveNumber
					? {
							name: 'archiveNumber',
							label: translate(dict => dict.projectMetadata.hso.archiveNumber.label),
							filterType: TableFilterType.Text,
							isOptional: true
					  }
					: null,
				showProjectType
					? {
							name: 'projectType',
							label: translate(dict => dict.projectMetadata.hso.projectType.label),
							filterType: TableFilterType.Text,
							isOptional: true
					  }
					: null
			].filter(notEmpty),
		[showSuborganization, showOrganization]
	);

	const initialVisibleColumns = [
		'projectName',
		'createdByUser',
		'collaborators',
		'codeList',
		'organization',
		'suborganization',
		'status',
		'archiveNumber',
		'projectType'
	];

	const { setNotification } = useAlerts();

	const { organization: orgName = '', subscriptionId: subscriptionId = '' } =
		useGetUserQuery().data?.user || {};
	const { data } = useGetAllUsersInEnterpriseProjects(orgName, subscriptionId);
	const enterpriseMembers = data?.enterpriseMembers ?? [];
	return (
		<>
			<Header.Main />
			<Header.Navigation
				rightComponent={() =>
					projects.length && !loadingEnterpriseProjects ? (
						<Flex>
							<SearchInput
								usedInHeader
								term={searchTerm}
								placeholder={translate(dict => dict.terms.search)}
								onChangeTerm={value => setSearchTerm(value)}
							/>
						</Flex>
					) : undefined
				}
			/>
			<Header.Title
				title={translate(dict => dict.enterpriseAdmin.header.menuTabs.projects)}
				component={
					exportProjects && (
						<Container>
							<Dropdown
								toggleComponent={({ ref, open, toggle }) => (
									<Icon
										variant={v => v.button}
										ref={ref}
										svg={Svgs.More}
										active={open}
										onClick={toggle}
									/>
								)}
								width={18}
								offset={{ top: 20, right: 0 }}
							>
								<>
									<Dropdown.Item
										title={dictionary.exportProjects}
										onClick={() => {
											if (!filteredProjects.length) {
												setNotification({
													message: dictionary.couldntExportProjects
												});
											} else {
												exportEnterpriseProjects(
													filteredProjects,
													projectMetadataCategoriesDict,
													translate,
													language
												);
											}
										}}
									/>
									<Dropdown.Item
										title={dictionary.exportProjectCollaborators}
										onClick={() => {
											if (
												!filteredProjects.length ||
												!enterpriseMembers.length
											) {
												setNotification({
													message:
														dictionary.couldntExportProjectCollaborators
												});
											} else {
												exportProjectCollaborators(
													enterpriseMembers,
													filteredProjects,
													language
												);
											}
										}}
									/>
								</>
							</Dropdown>
						</Container>
					)
				}
			/>

			<Suspend loading={false} immediate={false}>
				<Grid.Container>
					<CustomisableTable
						tableName={TableName.EnterpriseAdminProjects}
						items={tableItems}
						columns={columns}
						itemsCountLabel={translate(dict => dict.tableLists.projects)}
						initVisibleColumns={initialVisibleColumns}
						customCells={customCells}
						hasPagination
						extraCells={actionCellByRowId}
						activeExtraCell={activeDropownByProjectId ?? undefined}
						loading={loadingEnterpriseProjects}
						noItemsMessage={translate(dict => dict.projectList.noProjectsToShow)}
					/>
				</Grid.Container>
			</Suspend>
			{projectToTransfer && projectToTransfer.projectOwnerDetails && (
				<TransferProjectOwnershipModal
					projectId={projectToTransfer.projectId}
					currentOwnerDetails={projectToTransfer.projectOwnerDetails}
					onTransferred={() => getAllEnterpriseOwnedProjects()}
					onClose={() => {
						setProjectToTransfer(null);
						getAllEnterpriseOwnedProjects();
					}}
				/>
			)}
			{projectForFreezeModal && (
				<FreezeProjectModal
					projectId={projectForFreezeModal.projectId}
					projectName={projectForFreezeModal.projectName}
					onClose={() => {
						setProjectForFreezeModal(null);
						getAllEnterpriseOwnedProjects();
					}}
					visible={!!projectForFreezeModal}
				/>
			)}
			{projectForUnfreezeModal && (
				<UnfreezeProjectModal
					projectId={projectForUnfreezeModal.projectId}
					projectName={projectForUnfreezeModal.projectName}
					onClose={() => {
						setProjectForUnfreezeModal(null);
						getAllEnterpriseOwnedProjects();
					}}
					visible={!!projectForUnfreezeModal}
				/>
			)}
		</>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		exportProjects: 'Export Projects',
		exportProjectCollaborators: 'Export Project Collaborators',
		couldntExportProjects:
			'No projects found. Please check that there are projects before trying to export again.',
		couldntExportProjectCollaborators:
			'No collaborators found. Please check that there are registered collaborators before trying to export again.',
		pii: 'PII (personal identifiable information)',
		enabled: 'Enabled',
		disabled: 'Disabled'
	},
	[LanguageType.Norwegian]: {
		exportProjects: 'Eksporter Prosjekter',
		exportProjectCollaborators: 'Eksporter Prosjekt Samarbeidspartnere',
		couldntExportProjects:
			'Ingen prosjekter ble funnet. Vennligst sjekk at det finnes prosjekter før du prøver å eksportere igjen.',
		couldntExportProjectCollaborators:
			'Ingen samarbeidspartnere ble funnet. Vennligst sjekk at det finnes registrerte samarbeidspartnere før du prøver å eksportere igjen.',
		pii: 'PII (personlig identifiserbar informasjon)',
		enabled: 'Aktivert',
		disabled: 'Deaktivert'
	}
};
